/**
 * Design Library — registry.
 *
 * Indexes the pattern catalog by id, family, tag and capability, and validates
 * the whole library at load (fields + genome cross-refs + relationship refs),
 * so a malformed pattern fails fast rather than reaching the Thinking Engine.
 *
 * All lookups are O(1)/indexed, so growing the catalog to 1000+ patterns
 * changes nothing structurally.
 */

import { ALL_PATTERNS } from './patterns';
import { validatePattern } from './validatePattern';
import { GENOMES } from '../genome';

const GENOME_IDS = new Set(GENOMES.map((g) => g.id));
const PATTERN_IDS = new Set(ALL_PATTERNS.map((p) => p.id));

// Fail fast: every pattern must satisfy the frozen library contract.
const duplicates = ALL_PATTERNS.map((p) => p.id).filter((id, i, arr) => arr.indexOf(id) !== i);
if (duplicates.length) throw new Error(`Duplicate Design Library pattern id(s): ${[...new Set(duplicates)].join(', ')}`);

ALL_PATTERNS.forEach((pattern) => {
  const { valid, errors } = validatePattern(pattern, { genomeIds: GENOME_IDS, patternIds: PATTERN_IDS });
  if (!valid) throw new Error(`Invalid Design Library pattern "${pattern && pattern.id}": ${errors.join('; ')}`);
});

const byId = new Map(ALL_PATTERNS.map((p) => [p.id, p]));

const groupBy = (keyFn) => {
  const map = new Map();
  ALL_PATTERNS.forEach((p) => {
    keyFn(p).forEach((k) => {
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(p);
    });
  });
  return map;
};

const byFamily = groupBy((p) => [p.family]);
const byTag = groupBy((p) => p.tags);
const byCapability = groupBy((p) => p.capabilities);

export { ALL_PATTERNS, PATTERN_IDS };

/** Look up one pattern by id (frozen), or null. */
export const getPattern = (id) => byId.get(id) || null;

/** Summaries of every pattern. */
export const listPatterns = () => ALL_PATTERNS.map(({ id, name, family, variantOf }) => ({ id, name, family, variantOf }));

export const patternsByFamily = (family) => [...(byFamily.get(family) || [])];
export const patternsByTag = (tag) => [...(byTag.get(tag) || [])];
export const patternsByCapability = (capability) => [...(byCapability.get(capability) || [])];

/** Variants of a base pattern. */
export const getVariants = (id) => ALL_PATTERNS.filter((p) => p.variantOf === id);

/** Relationship accessors — resolved to pattern objects. */
export const getCompatible = (id) => (getPattern(id)?.compatiblePatterns || []).map(getPattern).filter(Boolean);
export const getConflicts = (id) => (getPattern(id)?.incompatiblePatterns || []).map(getPattern).filter(Boolean);
export const getDependencies = (id) => (getPattern(id)?.dependencies || []).map(getPattern).filter(Boolean);
